import { FormControl, FormHelperText, FormLabel, Input, Stack, Textarea } from "@mui/joy";
import { parse } from "date-fns";
import { format, fromZonedTime } from "date-fns-tz";
import { ReactNode } from "react";
import { FieldValues, RegisterOptions, useController, useFormContext } from "react-hook-form";
import { NumericFormat } from "react-number-format";

import { useMeProfileQuery } from "../../hooks/profile";
import JoyDatePicker, { JoyTimePicker } from "../JoyDateField";
import FormGroup from "./FormGroup";
import MultipleValuesSelectedIndicator from "./MultipleValuesSelected";

type TextInputProps = {
  name: string;
  type?: string;
  label: ReactNode | null;
  registerOptions?: RegisterOptions<FieldValues, string>;
} & React.ComponentProps<typeof Input>;

type TextareaInputProps = {
  name: string;
  label: ReactNode | null;
  helperText?: string;
} & React.ComponentProps<typeof Textarea>;

type NumberInputProps = Omit<TextInputProps, "defaultValue"> & {
  min?: number;
  max?: number;
  step?: number;
  decimalScale?: number;
};

export default function TextInput({
  name,
  type,
  label,
  disabled,
  slotProps,
  registerOptions,
  ...props
}: TextInputProps) {
  const { register, formState, getFieldState } = useFormContext();
  const { input: inputSlotProps, ...otherSlotProps } = slotProps || {};
  const fieldState = getFieldState(name, formState);

  return (
    <FormControl error={Boolean(fieldState.error)}>
      {label !== null ? <FormLabel>{label}</FormLabel> : null}
      <Input
        type={type}
        slotProps={{
          input: {
            ...inputSlotProps,
            ...register(name, {
              ...registerOptions,
              disabled,
            }),
          },
          ...otherSlotProps,
        }}
        // helperText={fieldState.isTouched && fieldState.error?.message}
        {...props}
      />
      {fieldState.error?.message ? (
        <FormHelperText>{fieldState.error?.message}</FormHelperText>
      ) : null}
    </FormControl>
  );
}

export function NumberInput({ name, label, ...props }: NumberInputProps) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name, control });
  return (
    <FormControl error={Boolean(fieldState.error)}>
      {label !== null ? <FormLabel>{label}</FormLabel> : null}
      <NumericFormat
        customInput={Input}
        allowNegative={false}
        decimalScale={0}
        value={field.value ?? ""}
        onValueChange={(values, sourceInfo) => {
          if (sourceInfo.source === "prop") {
            return;
          }
          if (values.floatValue !== undefined) {
            field.onChange(values.floatValue);
          }
          // Not undefined: react-hook-form shows the default value for an
          // undefined field, so the input would snap back to its saved number.
          if (values.floatValue === undefined && values.value === "") {
            field.onChange(null);
          }
        }}
        onBlur={field.onBlur}
        {...props}
        type="tel"
      />
      {fieldState.error?.message ? (
        <FormHelperText>{fieldState.error?.message}</FormHelperText>
      ) : null}
    </FormControl>
  );
}

const convertCmToM = (cm: number) => cm / 100;
const convertCmToFtIn = (cm: number) => {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return { feet, inches };
};
const convertMToCm = (m: number) => m * 100;
const convertFtInToCm = (feet: number, inches: number) => feet * 12 * 2.54 + inches * 2.54;
const convertersByUnitType = {
  METERS: {
    toCm: convertMToCm,
    fromCm: convertCmToM,
  },
  FEET_INCHES: {
    toCm: convertFtInToCm,
    fromCm: convertCmToFtIn,
  },
};

export function HeightInput({
  name,
  label,
  unitType = "METERS",
}: {
  name: string;
  label: ReactNode | null;
  unitType?: "METERS" | "FEET_INCHES";
}) {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name, control });

  if (unitType === "METERS") {
    const converterFns = convertersByUnitType[unitType];
    const valueInDisplayUnits =
      field.value || field.value === 0 ? converterFns.fromCm(field.value) : null;

    return (
      <FormGroup error={fieldState.isTouched && Boolean(fieldState.error)}>
        {label !== null ? <FormLabel>{label}</FormLabel> : null}
        <MultipleValuesSelectedIndicator name={name} />
        <NumericFormat
          customInput={Input}
          suffix="m"
          allowNegative={false}
          decimalScale={2}
          fixedDecimalScale={false}
          value={valueInDisplayUnits}
          onValueChange={(values) => {
            if (values.floatValue !== undefined) {
              field.onChange(converterFns.toCm(values.floatValue));
            }
          }}
          onBlur={field.onBlur}
        />
        {fieldState.error?.message ? (
          <FormHelperText>{fieldState.error?.message}</FormHelperText>
        ) : null}
      </FormGroup>
    );
  } else {
    const converterFns = convertersByUnitType[unitType];
    const { feet, inches } = converterFns.fromCm(field.value);
    return (
      <FormGroup error={Boolean(fieldState.error)}>
        {label !== null ? <FormLabel>{label}</FormLabel> : null}
        <MultipleValuesSelectedIndicator name={name} />
        <Stack direction="row" spacing={1}>
          <NumericFormat
            value={feet}
            onValueChange={(values, sourceInfo) => {
              if (sourceInfo.source === "prop") {
                return;
              }
              if (values.floatValue !== undefined) {
                field.onChange(converterFns.toCm(values.floatValue, inches || 0));
              }
            }}
            allowNegative={false}
            decimalScale={0}
            customInput={Input}
            suffix={" ft"}
            error={Boolean(fieldState.error)}
            onBlur={field.onBlur}
          />
          <NumericFormat
            value={inches}
            onValueChange={(values, sourceInfo) => {
              if (sourceInfo.source === "prop") {
                return;
              }
              if (values.floatValue !== undefined) {
                field.onChange(converterFns.toCm(feet || 0, values.floatValue));
              }
            }}
            allowNegative={false}
            decimalScale={0}
            customInput={Input}
            isAllowed={(values) => (values.floatValue ? values.floatValue <= 11 : true)}
            suffix={" in"}
            error={Boolean(fieldState.error)}
            onBlur={field.onBlur}
          />
        </Stack>
        {fieldState.error ? <FormHelperText>{fieldState.error?.message}</FormHelperText> : null}
      </FormGroup>
    );
  }
}

export function DateInput({
  name,
  label,
  timezone,
  minDate = "2000-01-01",
  maxDate = "2100-01-01",
  defaultTime = "00:00",
  withTime = true,
  asDateTime,
  placeholder,
  disabled,
  hideOpenButton,
}: {
  name: string;
  label: ReactNode | null;
  minDate?: string;
  maxDate?: string;
  timezone?: string; // if not set, use the browser timezone
  defaultTime?: string;
  withTime?: boolean;
  asDateTime?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Hide the calendar and clock buttons, for a narrow field where they'd crowd out the value. The
   * pickers still open when the fields are focused.
   */
  hideOpenButton?: boolean;
}) {
  asDateTime = asDateTime ?? withTime;
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });

  let valueAsDate: Date | null = null;
  if (field.value) {
    if (asDateTime) {
      // parse as ISO string
      valueAsDate = new Date(field.value);
    } else {
      // parse as date-only string
      valueAsDate = parse(field.value, "yyyy-MM-dd", new Date());
    }
  }

  placeholder = placeholder ?? (asDateTime ? "Any time" : "Any date");

  const dateStr = valueAsDate ? format(valueAsDate, "yyyy-MM-dd", { timeZone: timezone }) : "";
  const timeStr = valueAsDate ? format(valueAsDate, "HH:mm", { timeZone: timezone }) : "";

  if (minDate === "TODAY") {
    minDate = format(new Date(), "yyyy-MM-dd");
  }
  if (maxDate === "TODAY") {
    maxDate = format(new Date(), "yyyy-MM-dd");
  }

  return (
    <FormGroup error={Boolean(fieldState.error)}>
      {label !== null ? <FormLabel>{label}</FormLabel> : null}
      <MultipleValuesSelectedIndicator name={name} />
      <Stack direction="row" spacing={1}>
        <JoyDatePicker
          format="dd/MM/yyyy"
          // This is used as a placeholder when there is no value
          label={placeholder}
          sx={{ flex: 1, input: { width: 0 } }}
          minDate={new Date(minDate)}
          maxDate={new Date(maxDate)}
          value={valueAsDate}
          disabled={disabled}
          disableOpenPicker={hideOpenButton}
          onChange={(value) => {
            if (!value) {
              field.onChange(value);
              return;
            }
            if (timezone) {
              // convert value to timezone
              value = fromZonedTime(
                format(value, "yyyy-MM-dd") +
                  "T" +
                  (withTime ? timeStr || defaultTime : defaultTime),
                timezone,
              );
            } else {
              value = new Date(
                format(value, "yyyy-MM-dd") +
                  "T" +
                  (withTime ? timeStr || defaultTime : defaultTime),
              );
            }
            const dateStr = value.toISOString();
            field.onChange(asDateTime ? dateStr : format(value, "yyyy-MM-dd"));
          }}
        />

        {/* <Input
          sx={{
            flex: 1,
            "input::-webkit-date-and-time-value": { textAlign: "left" },
          }}
          slotProps={{
            input: {
              min: minDate,
            },
          }}
          type="date"
          value={dateStr}
          onChange={(e) => {
            let newValue: Date;
            if (timezone) {
              newValue = fromZonedTime(
                e.target.value +
                  "T" +
                  (withTime ? timeStr || defaultTime : defaultTime),
                timezone
              );
            } else {
              newValue = new Date(
                e.target.value +
                  "T" +
                  (withTime ? timeStr || defaultTime : defaultTime)
              );
            }
            const dateStr = newValue.toISOString();
            console.log(`onChange: ${name}`, dateStr);
            field.onChange(
              asDateTime ? dateStr : format(newValue, "yyyy-MM-dd")
            );
          }}
          onBlur={field.onBlur}
          error={Boolean(fieldState.error)}
        /> */}

        {/* {withTime ? (
          <Input
            sx={{ flex: 1 }}
            type="time"
            value={timeStr || defaultTime}
            onChange={(e) => {
              if (timezone) {
                const newValue = fromZonedTime(
                  (dateStr || format(new Date(), "YYYY-mm-dd")) +
                    "T" +
                    e.target.value,
                  timezone
                );
                field.onChange(newValue.toISOString());
              } else {
                const newValue = new Date(
                  (dateStr || format(new Date(), "YYYY-mm-dd")) +
                    "T" +
                    e.target.value
                );
                field.onChange(newValue.toISOString());
              }
            }}
            onBlur={field.onBlur}
            error={Boolean(fieldState.error)}
          />
        ) : null} */}
        {withTime ? (
          <JoyTimePicker
            sx={{ flex: 1, input: { width: 0 } }}
            value={valueAsDate}
            disabled={disabled}
            disableOpenPicker={hideOpenButton}
            onChange={(value) => {
              if (!value) {
                return;
              }
              if (timezone) {
                value = fromZonedTime(
                  (dateStr || format(new Date(), "yyyy-MM-dd")) + "T" + format(value, "HH:mm"),
                  timezone,
                );
              } else {
                value = new Date(
                  (dateStr || format(new Date(), "YYYY-mm-dd")) + "T" + format(value, "HH:mm"),
                );
              }
              field.onChange(value.toISOString());
            }}
          />
        ) : null}
      </Stack>
      {fieldState.error ? <FormHelperText>{fieldState.error?.message}</FormHelperText> : null}
    </FormGroup>
  );
}

export function TextareaInput({ name, label, helperText, ...props }: TextareaInputProps) {
  const { register, getFieldState } = useFormContext();
  const fieldState = getFieldState(name);
  return (
    <FormControl error={Boolean(fieldState.error)}>
      {label !== null ? <FormLabel>{label}</FormLabel> : null}
      <Textarea
        minRows={2}
        {...register(name)}
        error={Boolean(fieldState.error)}
        // helperText={meta.touched && meta.error}
        {...props}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {fieldState.error ? <FormHelperText>{fieldState.error?.message}</FormHelperText> : null}
    </FormControl>
  );
}

export function HeightInputAutoUnits({ name, label }: { name: string; label: string | null }) {
  const { data: userProfile } = useMeProfileQuery();
  const unitType = userProfile?.preferredUnits === "METRIC" ? "METERS" : "FEET_INCHES";
  return <HeightInput name={name} label={label} unitType={unitType} />;
}
